import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, type NumericFilterState, type OptionFilterState, type PageSize, type SearchSort, type SearchUrlState } from './types'

const NUMERIC_MIN_PREFIX = 'nmin:'
const NUMERIC_MAX_PREFIX = 'nmax:'
const OPTION_FILTER_PREFIX = 'of:'
const SEARCH_SORTS = new Set<SearchSort>(['updated-desc', 'name-asc', 'name-desc', 'price-asc', 'price-desc'])

export const parseSearchUrlState = (search: string): SearchUrlState => {
  const params = new URLSearchParams(search)
  const parsedPage = Number.parseInt(params.get('page') ?? '1', 10)
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1
  const parsedPageSize = Number.parseInt(params.get('limit') ?? String(DEFAULT_PAGE_SIZE), 10)
  const pageSize: PageSize = PAGE_SIZE_OPTIONS.includes(parsedPageSize as PageSize)
    ? parsedPageSize as PageSize
    : DEFAULT_PAGE_SIZE
  const numericFilters: NumericFilterState = {}
  const optionFilters: OptionFilterState = {}

  for (const [key, rawValue] of params.entries()) {
    const value = rawValue.trim()
    if (!value) continue
    if (key.startsWith(NUMERIC_MIN_PREFIX)) {
      const id = key.slice(NUMERIC_MIN_PREFIX.length).trim()
      if (!id) continue
      const current = numericFilters[id] ?? { min: '', max: '' }
      numericFilters[id] = { ...current, min: value }
      continue
    }
    if (key.startsWith(NUMERIC_MAX_PREFIX)) {
      const id = key.slice(NUMERIC_MAX_PREFIX.length).trim()
      if (!id) continue
      const current = numericFilters[id] ?? { min: '', max: '' }
      numericFilters[id] = { ...current, max: value }
      continue
    }
    if (key.startsWith(OPTION_FILTER_PREFIX)) {
      const id = key.slice(OPTION_FILTER_PREFIX.length).trim()
      if (!id) continue
      const current = optionFilters[id] ?? []
      optionFilters[id] = current.includes(value) ? current : [...current, value]
    }
  }

  const selectedTypes = Array.from(
    new Set(
      params
        .getAll('type')
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  )
  const selectedBrands = Array.from(
    new Set(
      params
        .getAll('brand')
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  )

  return {
    searchTerm: params.get('search')?.trim() ?? '',
    page,
    pageSize,
    selectedBrands,
    selectedTypes,
    priceRange: {
      min: params.get('price_min')?.trim() ?? '',
      max: params.get('price_max')?.trim() ?? '',
    },
    numericFilters,
    optionFilters,
    sort: SEARCH_SORTS.has(params.get('sort') as SearchSort) ? (params.get('sort') as SearchSort) : 'updated-desc',
  }
}

export const serializeSearchUrlState = ({
  searchTerm,
  page,
  pageSize,
  selectedBrands,
  selectedTypes,
  priceRange,
  numericFilters,
  optionFilters,
  sort,
}: SearchUrlState) => {
  const params = new URLSearchParams()
  const trimmedSearch = searchTerm.trim()
  if (trimmedSearch) params.set('search', trimmedSearch)
  if (page > 1) params.set('page', String(page))
  if (pageSize !== DEFAULT_PAGE_SIZE) params.set('limit', String(pageSize))
  if (sort !== 'updated-desc') params.set('sort', sort)

  Array.from(
    new Set(
      selectedBrands
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  )
    .sort((left, right) => left.localeCompare(right, 'it', { sensitivity: 'base' }))
    .forEach((value) => params.append('brand', value))

  Array.from(
    new Set(
      selectedTypes
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  )
    .sort((left, right) => left.localeCompare(right, 'it', { sensitivity: 'base' }))
    .forEach((value) => params.append('type', value))

  const minPrice = priceRange.min.trim()
  const maxPrice = priceRange.max.trim()
  if (minPrice) params.set('price_min', minPrice)
  if (maxPrice) params.set('price_max', maxPrice)

  Object.entries(numericFilters)
    .sort(([left], [right]) => left.localeCompare(right, 'it', { sensitivity: 'base' }))
    .forEach(([id, range]) => {
      const min = range.min.trim()
      const max = range.max.trim()
      if (min) params.set(`${NUMERIC_MIN_PREFIX}${id}`, min)
      if (max) params.set(`${NUMERIC_MAX_PREFIX}${id}`, max)
    })

  Object.entries(optionFilters)
    .sort(([left], [right]) => left.localeCompare(right, 'it', { sensitivity: 'base' }))
    .forEach(([id, values]) => {
      Array.from(
        new Set(
          values
            .map((value) => value.trim())
            .filter(Boolean),
        ),
      )
        .sort((left, right) => left.localeCompare(right, 'it', { sensitivity: 'base' }))
        .forEach((value) => params.append(`${OPTION_FILTER_PREFIX}${id}`, value))
    })

  return params.toString()
}
